<script setup lang="ts">
import dayjs from 'dayjs'
import { resolveAppCookieDomain } from '~/composables/useAppCookie'

type ChromeDisplayCount = {
  num: number
  time: number
}

const CHROME_PLUGIN_MARKER_ID = 'jflkdlbjgfiifajbjeockfjhbnhlakfd'
const CHROME_DISPLAY_COOKIE_NAME = 'chrome_count'
const CHROME_MAX_AUTO_SHOW_COUNT = 3
const CHROME_RELOAD_GUARD_MS = 500
const CHROME_PLUGIN_ICON =
  'https://resources1.interface003.com/web30/2026/08/19/fa6e3a4e-9a1d-46a4-bf43-fec788e0edbe.svg'
const CHROME_PLUGIN_CORNER_ICON =
  'https://resources1.interface003.com/web30/2026/08/19/0e731c10-6def-497f-95cb-cb530e229ecb.svg'
const CHROME_WEB_STORE_URL = 'https://chrome.google.com/webstore/detail/wikibit/kgjhhnlcfjcjpdmfdhmhabdjbfpicppg'

const appStore = useAppStore()
const { t } = useAppI18n()
const chromeDisplayCookie = useAppObjectCookie<ChromeDisplayCount>(CHROME_DISPLAY_COOKIE_NAME, null)
const legacyChromeDisplayCookie = useAppObjectCookie<ChromeDisplayCount>(`chrome_${dayjs().format('YYYYMMDD')}`, null)

// 组件只在桌面 Chrome 且插件未安装时展示；弹层状态与浮动入口分开维护。
const showChromeDownload = ref(false)
const shouldShowChromeEntry = ref(false)
const isReady = ref(false)
const scrollListenerReadyTimer = ref<ReturnType<typeof window.setTimeout> | null>(null)

onMounted(() => {
  cleanupLegacyChromeCookies()

  const revealChromeEntry = () => {
    if (!isChromeBrowser() || document.getElementById(CHROME_PLUGIN_MARKER_ID)) {
      isReady.value = true
      return
    }

    initializeChromePluginEntry()
    isReady.value = true
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(revealChromeEntry)
  } else {
    window.setTimeout(revealChromeEntry, 200)
  }

  // 延迟绑定滚动收起，避免首屏初始化阶段的浏览器滚动修正误关弹层。
  scrollListenerReadyTimer.value = window.setTimeout(() => {
    window.addEventListener('scroll', closeOnScroll, { passive: true })
  }, 1500)
})

onBeforeUnmount(() => {
  if (scrollListenerReadyTimer.value !== null) {
    window.clearTimeout(scrollListenerReadyTimer.value)
  }
  window.removeEventListener('scroll', closeOnScroll)
})

/** 判断当前浏览器是否为 Chrome，并排除 Chromium Edge、Opera 和移动端浏览器。 */
const isChromeBrowser = () => {
  const userAgent = navigator.userAgent
  const isChromium = userAgent.includes('Chrome') || userAgent.includes('CriOS')
  const isExcludedBrowser = userAgent.includes('Edg') || userAgent.includes('OPR') || userAgent.includes('Opera')
  const isMobileBrowser = /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent)

  return isChromium && !isExcludedBrowser && !isMobileBrowser
}

/** 兼容读取历史按日期保存的展示计数，并将异常值视为空。 */
const getChromeDisplayCount = (): ChromeDisplayCount | null => {
  const currentCount = chromeDisplayCookie.value
  if (currentCount && Number.isFinite(currentCount.num) && Number.isFinite(currentCount.time)) {
    return currentCount
  }

  const legacyCount = legacyChromeDisplayCookie.value
  return legacyCount && Number.isFinite(legacyCount.num) && Number.isFinite(legacyCount.time) ? legacyCount : null
}

/** 首次进入首页时最多展示入口三次，并避免客户端短时间重复挂载导致多计数。 */
const initializeChromePluginEntry = () => {
  if (!isChromeBrowser()) {
    return
  }

  const currentCount = getChromeDisplayCount()
  const currentTime = Date.now()

  if (!currentCount) {
    chromeDisplayCookie.value = { num: 1, time: currentTime }
    shouldShowChromeEntry.value = true
    return
  }

  if (currentCount.num < CHROME_MAX_AUTO_SHOW_COUNT) {
    shouldShowChromeEntry.value = true

    if (currentTime > currentCount.time + CHROME_RELOAD_GUARD_MS) {
      chromeDisplayCookie.value = { num: currentCount.num + 1, time: currentTime }
    }
    return
  }

  if (currentCount.num === CHROME_MAX_AUTO_SHOW_COUNT && currentTime < currentCount.time + CHROME_RELOAD_GUARD_MS) {
    shouldShowChromeEntry.value = true
  }
}

/** 删除旧版 `chrome_YYYYMMDD` 等历史 Cookie，统一迁移到 `chrome_count`。 */
const cleanupLegacyChromeCookies = () => {
  if (!document.cookie) {
    return
  }

  const cookieDomain = resolveAppCookieDomain(window.location.hostname)

  for (const cookiePair of document.cookie.split('; ')) {
    const separatorIndex = cookiePair.indexOf('=')
    const cookieName = separatorIndex >= 0 ? cookiePair.slice(0, separatorIndex) : cookiePair
    if (cookieName.startsWith('chrome_') && cookieName !== CHROME_DISPLAY_COOKIE_NAME) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`
      if (cookieDomain) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${cookieDomain}`
      }
    }
  }
}

/** 点击右侧入口时切换插件说明弹层。 */
const toggleChromeDownload = () => {
  showChromeDownload.value = !showChromeDownload.value
}

/** 关闭插件说明弹层，但保留右侧入口供用户再次打开。 */
const closeChromeDownload = () => {
  showChromeDownload.value = false
}

/** 页面滚动后收起弹层，避免遮挡用户继续浏览首页内容。 */
const closeOnScroll = () => {
  if (showChromeDownload.value) {
    showChromeDownload.value = false
  }
}

/** 国内访问插件落地页，其他地区直达 Chrome Web Store。 */
const downloadChromePlugin = () => {
  const isChina = ['cn', '156'].includes(appStore.myCountry.toLowerCase())
  const downloadUrl = isChina
    ? `https://plugins.chajianapi.com/trade/bit.html?${new URLSearchParams({
        LanguageCode: 'zh-cn',
        CountryCode: appStore.CountryCode || 'cn',
      }).toString()}`
    : CHROME_WEB_STORE_URL

  navigateTo(downloadUrl, { external: true, open: { target: '_blank' } })
}
</script>

<template>
  <aside
    v-if="isReady && shouldShowChromeEntry"
    class="chrome-plugin fixed top-[50%] z-[40] hidden h-[114px] w-[56px] -translate-y-1/2 lg:block"
    :aria-label="t('25735')"
  >
    <button
      type="button"
      class="chrome-plugin-entry relative flex h-[114px] w-[56px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-l-[12px] border-0 bg-white p-[0px] shadow-[0_0_14px_rgba(0,0,0,0.08)] transition-colors hover:bg-[#eeeeee] rtl:rounded-l-[0px] rtl:rounded-r-[12px]"
      :aria-expanded="showChromeDownload"
      @click.stop="toggleChromeDownload"
    >
      <span
        class="absolute top-[0px] left-[0px] flex h-[21px] w-[27px] items-center justify-center rounded-tl-[11px] rounded-br-[11px] bg-[#4889f4] rtl:right-[0px] rtl:left-auto rtl:rounded-tl-[0px] rtl:rounded-tr-[11px] rtl:rounded-br-[0px] rtl:rounded-bl-[11px]"
      >
        <CommonImage
          :src="CHROME_PLUGIN_CORNER_ICON"
          alt=""
          width="14"
          height="14"
          loading="eager"
          object-fit="contain"
          :fallback-src="false"
        />
      </span>
      <CommonImage
        :src="CHROME_PLUGIN_ICON"
        alt="Chrome"
        class="size-[26px]"
        width="26"
        height="26"
        loading="eager"
        object-fit="contain"
        :fallback-src="false"
      />
      <span class="mt-[3px] h-[14px] text-[10px] leading-[14px] font-semibold text-[#3d3d3d]">Chrome</span>
    </button>

    <Transition name="chrome-plugin-panel">
      <div
        v-if="showChromeDownload"
        class="chrome-plugin-panel absolute bottom-[-60px] flex w-[348px] items-center justify-center rounded-[12px] bg-white drop-shadow-[0_0_14px_rgba(0,0,0,0.1)]"
      >
        <div class="mx-[20px] flex flex-col items-center text-center">
          <p dir="auto" class="mt-[16px] text-[20px] leading-[28px] font-semibold text-black">
            {{ t('85741') }}
          </p>
          <p dir="auto" class="mt-[2px] mb-[22px] text-[14px] leading-[20px] font-normal text-[#9a9a9c]">
            {{ t('82528') }}
          </p>
          <p v-if="appStore.LanguageCode === 'zh-cn'" class="text-[16px] leading-[24px] font-normal text-[#292929]">
            浏览加密交易所网站
          </p>
          <p v-if="appStore.LanguageCode === 'zh-cn'" class="text-[16px] leading-[24px] font-normal text-[#292929]">
            精准识别交易所真假优劣
          </p>
          <p
            v-if="appStore.LanguageCode !== 'zh-cn'"
            dir="auto"
            class="text-[16px] leading-[24px] font-normal text-[#292929]"
          >
            {{ t('85742') }}
          </p>
          <button
            type="button"
            class="mt-[16px] mb-[28px] flex cursor-pointer items-center rounded-[69px] border border-solid border-[rgba(0,0,0,0.6)] bg-white px-[20px] py-[6px] transition-colors hover:bg-[#eeeeee]"
            @click.stop="downloadChromePlugin"
          >
            <CommonImage
              :src="CHROME_PLUGIN_ICON"
              alt=""
              class="size-[32px]"
              width="32"
              height="32"
              loading="eager"
              object-fit="contain"
              :fallback-src="false"
            />
            <span dir="auto" class="ml-[7px] text-[16px] leading-[22px] font-semibold text-black">
              {{ t('85743') }}
            </span>
          </button>
        </div>

        <button
          type="button"
          class="absolute top-[12px] right-[12px] flex size-[22px] cursor-pointer items-center justify-center rounded-[4px] border border-solid border-[rgba(0,0,0,0.2)] bg-white p-[2px] text-[#3d3d3d] transition-colors hover:bg-[#eeeeee]"
          :aria-label="t('83802')"
          @click.stop="closeChromeDownload"
        >
          <img
            src="https://resources1.interface003.com/web30/2026/08/12/e86f8de6-09b8-4fa3-ac01-3be563cfd764.svg"
            alt=""
          />
        </button>
      </div>
    </Transition>
  </aside>
</template>

<style scoped lang="less">
.chrome-plugin {
  right: 0;
  left: auto;
}

.chrome-plugin-panel {
  right: 68px;
  left: auto;
  direction: ltr;
}

.chrome-plugin-entry {
  &::before {
    position: absolute;
    inset: 0;
    content: '';
    pointer-events: none;
    background-image: url('https://resources1.interface003.com/web20/img/othor/chorme_bg.png');
    background-repeat: no-repeat;
  }
}

.chrome-plugin-panel-enter-active,
.chrome-plugin-panel-leave-active {
  transition: opacity 0.2s ease;
}

.chrome-plugin-panel-enter-from,
.chrome-plugin-panel-leave-to {
  opacity: 0;
}

html[dir='rtl'] {
  .chrome-plugin {
    right: auto;
    left: 0;
  }

  .chrome-plugin-panel {
    right: auto;
    left: 68px;
  }

  .chrome-plugin-entry::before {
    transform: scaleX(-1);
  }
}
</style>
